/**
 * catalogService — Service layer per leggere e modificare il catalogo
 * commerciale (MOD-2-B).
 *
 * Vive in `shared/` perche' usato sia dal frontend (console + app utente
 * via firebase/firestore client) sia dal backend (functions via
 * firebase-admin). Le 2 implementazioni divergono solo sulla firma
 * delle API Firestore.
 *
 * Strategia di import:
 *  - Frontend usa `catalogService.client.ts` (re-export di funzioni con
 *    firebase/firestore client SDK)
 *  - Backend usa `catalogService.admin.ts` (re-export con firebase-admin)
 *
 * Questo file espone interfacce pure (tipi + signature) e logica
 * condivisa NON-Firestore-specific. La caching layer e' anch'essa qui
 * perche' indipendente dall'SDK.
 *
 * @since 2026-05-17
 */

import type {
  ModuleDescriptor,
  FeatureDescriptor,
  SettingDescriptor,
} from '../types/catalog.types';

// ============================================================================
// COSTANTI
// ============================================================================

export const CATALOG_COLLECTIONS = {
  modules: 'mkt_catalog_modules',
  features: 'mkt_catalog_features',
  settings: 'mkt_catalog_settings',
} as const;

/**
 * Tempo di vita della cache in-memory (5 minuti).
 * Il catalogo cambia raramente (modifiche super_admin), quindi cache
 * aggressiva e' OK. Per forzare reload chiamare `invalidateCatalogCache()`.
 */
export const CATALOG_CACHE_TTL_MS = 5 * 60 * 1000;

// ============================================================================
// CATALOGO COMPLETO (output del service)
// ============================================================================

export interface CatalogSnapshot {
  modules: ModuleDescriptor[];
  features: FeatureDescriptor[];
  settings: SettingDescriptor[];
  loaded_at: number; // timestamp ms
}

// ============================================================================
// CACHE IN-MEMORY (module-level, shared per processo)
// ============================================================================

interface CacheEntry {
  data: CatalogSnapshot;
  expires_at: number;
}

let cache: CacheEntry | null = null;

export function getCachedCatalog(): CatalogSnapshot | null {
  if (!cache) return null;
  if (Date.now() > cache.expires_at) {
    cache = null;
    return null;
  }
  return cache.data;
}

export function setCachedCatalog(snapshot: CatalogSnapshot): void {
  cache = {
    data: snapshot,
    expires_at: Date.now() + CATALOG_CACHE_TTL_MS,
  };
}

export function invalidateCatalogCache(): void {
  cache = null;
}

// ============================================================================
// HELPER LOGICI (puri, riusabili FE + BE)
// ============================================================================

/**
 * Ritorna tutte le feature appartenenti a un modulo specifico.
 */
export function getFeaturesOfModule(
  snapshot: CatalogSnapshot,
  moduleId: string,
): FeatureDescriptor[] {
  return snapshot.features.filter((f) => f.parent_module === moduleId);
}

/**
 * Ritorna un modulo per id.
 */
export function getModuleById(
  snapshot: CatalogSnapshot,
  moduleId: string,
): ModuleDescriptor | undefined {
  return snapshot.modules.find((m) => m.id === moduleId);
}

/**
 * Ritorna una feature per id.
 */
export function getFeatureById(
  snapshot: CatalogSnapshot,
  featureId: string,
): FeatureDescriptor | undefined {
  return snapshot.features.find((f) => f.id === featureId);
}

/**
 * Ritorna un setting per id.
 */
export function getSettingById(
  snapshot: CatalogSnapshot,
  settingId: string,
): SettingDescriptor | undefined {
  return snapshot.settings.find((s) => s.id === settingId);
}

/**
 * Ritorna i moduli di una categoria specifica.
 */
export function getModulesByCategory(
  snapshot: CatalogSnapshot,
  category: ModuleDescriptor['category'],
): ModuleDescriptor[] {
  return snapshot.modules.filter((m) => m.category === category);
}

/**
 * Conta totali per tipo (utile per dashboard catalogo).
 */
export function getCatalogStats(snapshot: CatalogSnapshot): {
  modules: number;
  features: number;
  settings: number;
  total: number;
  by_status: Record<string, number>;
} {
  // I settings non hanno `status`, contiamo solo modules + features
  const itemsWithStatus = [...snapshot.modules, ...snapshot.features];
  const by_status: Record<string, number> = {};
  for (const item of itemsWithStatus) {
    by_status[item.status] = (by_status[item.status] ?? 0) + 1;
  }
  return {
    modules: snapshot.modules.length,
    features: snapshot.features.length,
    settings: snapshot.settings.length,
    total: snapshot.modules.length + snapshot.features.length + snapshot.settings.length,
    by_status,
  };
}
